// The context beside a conversation (Session AUB, Item 4). For the customer the sender match names OUTRIGHT (confirmed by
// the office, or an exact recorded address), it reads the customer's account through the EXISTING reads only: the
// customer overview (GET /api/customers/:id/overview, through CustomerOverviewTab itself, so its sections, links and
// rules are never copied here) and the customer's contacts (GET /api/contacts). Nothing about the customer is worked out on
// the client.
//
// A SUGGESTION is never treated as the customer: a domain-only match lists its candidates, each confirmed by a person.
// A match is what the office sees beside the conversation; it never changes what a draft may say (the FACTS trust rule,
// D142, is decided on the server from the sender's own recorded address).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MailMessageDetail, MailTarget } from "../../../lib/api";
import { api } from "../../../lib/api";
import { contactsApi, type Contact } from "../../../lib/contacts";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { CustomerOverviewTab } from "../CustomerOverviewTab";
import { CustomerPicker, matchedCustomer } from "./mailUi";

const BTN = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-strong transition hover:bg-ele-inset max-sm:min-h-11";

export function MailContextPanel({ detail, onMatched }: { detail: MailMessageDetail; onMatched: () => void }) {
  const customer = matchedCustomer(detail.match);
  const target: MailTarget = { messageId: detail.messageId, enquiryId: detail.enquiryId };
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function confirm(customerId: string) {
    setBusy(customerId);
    setError(null);
    try {
      await api.mail.match(target, customerId);
      onMatched();
    } catch (err) {
      setError(friendlyApiError(err, "The match could not be saved. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-label="Context" className="space-y-4">
      <div className="rounded-ele border border-ele-hairline bg-ele-inset p-3 text-sm">
        <h3 className="font-heading text-base text-ele-strong">Customer</h3>
        {customer ? (
          <p className="mt-1">
            <Link to={`/office/customers/${encodeURIComponent(customer.customerId)}`} className="text-ele-blue underline-offset-2 hover:underline">
              {customer.customerName || "Open the customer"}
            </Link>
            {customer.siteName ? <span className="text-ele-body"> · {customer.siteName}</span> : null}
            <span className="block text-xs text-ele-body">
              {detail.match.kind === "confirmed" ? "Matched by the office." : "Matched on the customer's recorded address."}
            </span>
          </p>
        ) : (
          <p className="mt-1 text-ele-body">No customer matched.</p>
        )}

        {(detail.match.kind === "suggestion" || detail.match.kind === "ambiguous") && (
          <div className="mt-3">
            <p className="text-xs text-ele-body">
              {detail.match.kind === "suggestion"
                ? `The sender's domain (${detail.match.domain}) matches ${detail.match.candidates.length === 1 ? "this customer" : "these customers"}. Confirm one only if you know it is right.`
                : `The address ${detail.match.address} is recorded on more than one customer. Confirm which one this is.`}
            </p>
            <ul className="mt-2 space-y-1.5">
              {detail.match.candidates.map((c) => (
                <li key={c.customerId}>
                  <button type="button" disabled={busy != null} onClick={() => void confirm(c.customerId)} className={BTN}>
                    {busy === c.customerId ? "Saving..." : `Confirm ${c.customerName}`}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-3">
          <p className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body">{customer ? "Choose a different customer" : "Choose a customer"}</p>
          <CustomerPicker label="Choose a customer for this conversation" value={null} onChange={(c) => c && void confirm(c.id)} />
          <p className="mt-1 text-xs text-ele-body">
            A match is what the office sees beside the conversation. It never changes what a draft may say.
          </p>
        </div>
        {error && (
          <p role="alert" className="mt-2 text-xs text-ele-danger">
            {error}
          </p>
        )}
      </div>

      {customer && (
        <>
          <ContactsList customerId={customer.customerId} />
          <CustomerOverviewTab customerId={customer.customerId} />
        </>
      )}
    </section>
  );
}

function ContactsList({ customerId }: { customerId: string }) {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setContacts(null);
    setFailed(false);
    contactsApi
      .list({ customerId })
      .then((rows) => live && setContacts(rows))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [customerId]);

  return (
    <section>
      <h2 className="mb-2 font-heading text-lg tracking-wide text-ele-strong">Contacts</h2>
      {failed ? (
        <p className="text-sm text-ele-body">The contacts could not be loaded.</p>
      ) : contacts == null ? (
        <p className="text-sm text-ele-body">Loading the contacts...</p>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">No contacts recorded for this customer.</p>
      ) : (
        <ul className="space-y-1.5">
          {contacts.map((c) => (
            <li key={c.id} className="rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm">
              <span className="block truncate text-ele-strong">
                {c.name} <span className="text-xs text-ele-body">· {c.roleLabel}</span>
              </span>
              <span className="mt-0.5 block text-xs text-ele-body [overflow-wrap:anywhere]">
                {[c.email, c.phone, c.siteName].filter(Boolean).join(" · ") || "No email or phone recorded"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
